import React, { useEffect, useState, useMemo, useRef, forwardRef } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Snackbar,
  Stepper,
  Step,
  StepLabel,
  StepConnector,
  Typography,
  Slide,
  Fade,
  Divider,
  CircularProgress,
  Portal,
} from "@mui/material";
import Autocomplete, { createFilterOptions } from "@mui/material/Autocomplete";
import CheckOutlinedIcon from "@mui/icons-material/CheckOutlined";
import { useTheme } from "@mui/material";
import { tokens } from "../../theme";
import { Formik } from "formik";
import * as yup from "yup";
import {
  createSalesRecord,
  updateSalesRecord,
} from "../../services/SaleService";
import { listProducts } from "../../services/ProductService";
import CustomerForm from "../Customer/CustomerForm";
import { listCustomer } from "../../services/CustomerService";
import CustomDatePicker from "../CustomDatePicker";

// Transition for the Dialog (slides up)
const Transition = forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

// Transition for the Snackbar (slides in from the right)
function SlideTransition(props) {
  return <Slide {...props} direction="left" />;
}

// minimal validation
const validationSchema = yup.object().shape({
  customerEmail: yup.string().notRequired(),
  date: yup.string().required("Date is required"),
});

const steps = [
  "Customer Information",
  "Product Information",
  "Payment Details",
];

const SalesRecordForm = ({
  open,
  handleClose,
  onRecordAdded,
  onRecordEdited,
  editRecord,
}) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  // Cache the editMode so that the title doesn't flicker while the dialog closes
  const [wasEditMode, setWasEditMode] = useState(Boolean(editRecord));
  useEffect(() => {
    if (open) {
      setWasEditMode(Boolean(editRecord));
    }
  }, [open, editRecord]);
  const editMode = open ? Boolean(editRecord) : wasEditMode;

  const [phase, setPhase] = useState("idle");
  const [products, setProducts] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [openCustomerModal, setOpenCustomerModal] = useState(false);
  const [customers, setCustomers] = useState([]);
  const [selectedCustomerId, setSelectedCustomerId] = useState(
    editRecord ? editRecord.customerId : null,
  );
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const [activeStep, setActiveStep] = useState(0);
  const [prefillCustomer, setPrefillCustomer] = useState(null);
  const [customerAutocompleteOpen, setCustomerAutocompleteOpen] =
    useState(false);
  const [customerDialogPrefill, setCustomerDialogPrefill] = useState(null);

  const [selectedCustomerObj, setSelectedCustomerObj] = useState(
    editRecord
      ? {
          id: editRecord.customerId,
          fullName: editRecord.customerName,
          email: editRecord.customerEmail || "",
        }
      : null,
  );

  const filter = createFilterOptions();
  const formikRef = useRef(null);

  useEffect(() => {
    const fetchProducts = async () => {
      const response = await listProducts();
      setProducts(response.data || []);
    };

    const fetchCustomers = async () => {
      const response = await listCustomer();
      setCustomers(response.data || []);
    };

    fetchProducts();
    fetchCustomers();
  }, []);

  useEffect(() => {
    if (editMode && editRecord && products.length > 0) {
      const product =
        products.find((p) => p.id === editRecord.productId) || null;
      setSelectedProduct(product);
    }
  }, [editMode, editRecord, products]);

  useEffect(() => {
    if (editRecord && customers && customers.length) {
      const found = customers.find((c) => c.id === editRecord.customerId);
      if (found) setSelectedCustomerObj(found);
    }
  }, [editRecord, customers]);

  useEffect(() => {
    if (!prefillCustomer) return;
    if (
      formikRef.current &&
      typeof formikRef.current.setFieldValue === "function"
    ) {
      formikRef.current.setFieldValue(
        "customerName",
        prefillCustomer.fullName || "",
      );
      formikRef.current.setFieldValue(
        "customerEmail",
        String(prefillCustomer.email || ""),
      );
      formikRef.current.setFieldValue("customerId", prefillCustomer.id || null);
    }
    setSelectedCustomerId(prefillCustomer.id || null);
    setPrefillCustomer(null);
  }, [prefillCustomer, formikRef]);

  const handleCloseCustomerModal = () => {
    setOpenCustomerModal(false);
    setCustomerDialogPrefill(null);
  };

  const handleCustomerAdded = (newCustomer) => {
    setCustomers((prev) => [...prev, newCustomer]);
    setSelectedCustomerId(newCustomer.id);
    setPrefillCustomer(newCustomer);
    setSelectedCustomerObj(newCustomer);
    setOpenCustomerModal(false);
    setCustomerDialogPrefill(null);
  };

  const uniqueCustomers = useMemo(() => {
    const map = new Map();
    (customers || []).forEach((c) => {
      if (c && c.id != null) map.set(c.id, c);
    });
    return Array.from(map.values());
  }, [customers]);

  const uniqueProducts = useMemo(() => {
    const map = new Map();
    (products || []).forEach((p) => {
      if (p && p.id != null) map.set(p.id, p);
    });
    return Array.from(map.values());
  }, [products]);

  const initialValues = {
    title: editRecord?.title || "",
    type: editRecord?.type || "",
    quantity: editRecord?.quantity || "",
    rate: editRecord?.rate || "",
    salesDesc: editRecord?.salesDesc || "",
    customerName: editRecord?.customerName || "",
    customerEmail: editRecord?.customerEmail || "",
    date: editRecord?.date || "",
    productId: editRecord?.productId || "",
    paymentStatus: editRecord?.paymentStatus || "PENDING_PAYMENT",
    amountPaid: editRecord?.amountPaid || "",
    outstandingBalance: editRecord?.outstandingBalance ?? "",
    installmentPlan: editRecord?.installmentPlan || "INACTIVE",
    paymentFrequency: editRecord?.paymentFrequency || "MONTHLY",
    installmentDuration: editRecord?.installmentDuration || "",
    customInstallmentDuration: editRecord?.customInstallmentDuration || "",
    customerId: editRecord?.customerId || selectedCustomerId || null,
  };

  const mapInstallmentDurationToEnum = (value) => {
    if (!value) return null;
    const normalized = String(value).trim().toLowerCase();
    switch (normalized) {
      case "1 month":
      case "one month":
        return "ONE_MONTH";
      case "3 months":
      case "three months":
        return "THREE_MONTHS";
      case "6 months":
      case "six months":
        return "SIX_MONTHS";
      case "1 year":
      case "one year":
        return "ONE_YEAR";
      case "custom":
        return "CUSTOM";
      default:
        return String(value).toUpperCase();
    }
  };

  const nextStep = (values, errors, setFieldTouched) => {
    if (activeStep === 0) {
      if (!selectedCustomerId && !selectedCustomerObj && !values.customerName) {
        setSnackbarMessage("Please select or add a customer.");
        setSnackbarSeverity("error");
        setSnackbarOpen(true);
        setFieldTouched("customerName", true, false);
        return;
      }
    }

    if (activeStep === 1) {
      const missing = [];
      if (!values.title) missing.push("Product Title");
      if (!values.type) missing.push("Type");
      if (!values.rate || Number(values.rate) < 0) missing.push("Rate");
      if (!values.quantity || Number(values.quantity) <= 0)
        missing.push("Quantity (> 0)");
      if (!values.date) missing.push("Date");
      if (missing.length > 0) {
        setSnackbarMessage(`Please fill: ${missing.join(", ")}`);
        setSnackbarSeverity("error");
        setSnackbarOpen(true);
        missing.forEach(() => setFieldTouched("title", true, false));
        return;
      }
    }

    setActiveStep((s) => s + 1);
  };

  const prevStep = () => {
    setActiveStep((s) => Math.max(0, s - 1));
  };

  const handleSubmit = (values, { setSubmitting, resetForm }) => {
    setPhase("loading");
    return new Promise(async (resolve) => {
      try {
        if (!selectedProduct) {
          setSnackbarMessage(
            "Please select a product from the list or type an existing product name.",
          );
          setSnackbarSeverity("error");
          setSnackbarOpen(true);
          setPhase("idle");
          setSubmitting(false);
          resolve();
          return;
        }

        const installmentDurationEnum = mapInstallmentDurationToEnum(
          values.installmentDuration,
        );

        const salesData = {
          ...values,
          productId: selectedProduct ? selectedProduct.id : null,
          paymentStatus: values.paymentStatus,
          outstandingBalance: values.outstandingBalance,
          installmentPlan: values.installmentPlan,
          paymentFrequency: values.paymentFrequency || null,
          installmentDuration: installmentDurationEnum,
          customInstallmentDuration:
            values.customInstallmentDuration === ""
              ? null
              : Number(values.customInstallmentDuration),
          customerId: selectedCustomerId || values.customerId,
          customerName:
            selectedCustomerObj?.fullName || values.customerName || "",
          customerEmail:
            selectedCustomerObj?.email || values.customerEmail || "",
        };

        let response;
        if (editMode) {
          response = await updateSalesRecord(editRecord.id, salesData);
        } else {
          response = await createSalesRecord(salesData);
        }

        // Keep loading state visible for 1.5 seconds
        setTimeout(() => {
          setPhase("idle");
          resetForm();

          if (editMode) {
            onRecordEdited(response.data);
          } else {
            onRecordAdded(response.data);
          }

          handleCloseDialog();

          setTimeout(() => {
            setSnackbarMessage(
              `Sales record ${editMode ? "updated" : "created"} successfully.`,
            );
            setSnackbarSeverity("success");
            setSnackbarOpen(true);
            resolve();
          }, 300);
        }, 1500);
      } catch (error) {
        console.error(
          `Failed to ${editMode ? "update" : "create"} sales record: `,
          error,
        );
        setPhase("idle");
        setSnackbarMessage(
          `Failed to ${editMode ? "update" : "create"} sales record. Please try again.`,
        );
        setSnackbarSeverity("error");
        setSnackbarOpen(true);
        setSubmitting(false);
        resolve();
      }
    });
  };

  const handleCloseDialog = () => {
    setActiveStep(0);
    setPhase("idle");
    if (formikRef.current) formikRef.current.resetForm();
    setSelectedProduct(null);
    setSelectedCustomerObj(null);
    setSelectedCustomerId(null);
    setPrefillCustomer(null);
    handleClose();
  };

  const handleSnackbarClose = (_, reason) => {
    if (reason === "clickaway") return;
    setSnackbarOpen(false);
  };

  // Shared input styling matching other forms
  const inputStyleProps = {
    "& .MuiFilledInput-root": {
      backgroundColor:
        theme.palette.mode === "dark"
          ? "rgba(255,255,255,0.05)"
          : "rgba(0,0,0,0.04)",
      borderRadius: "8px",
      border: "1px solid transparent",
      transition: "all 0.2s ease",
      "&:hover": {
        backgroundColor:
          theme.palette.mode === "dark"
            ? "rgba(255,255,255,0.08)"
            : "rgba(0,0,0,0.06)",
      },
      "&.Mui-focused": {
        backgroundColor:
          theme.palette.mode === "dark"
            ? "rgba(255,255,255,0.08)"
            : "rgba(0,0,0,0.06)",
        borderColor: colors.blueAccent[500],
      },
    },
    "& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button":
      {
        display: "none",
        WebkitAppearance: "none",
        margin: 0,
      },
    "& input[type=number]": {
      MozAppearance: "textfield",
    },
  };

  const dropdownPaperStyle = {
    backgroundColor:
      theme.palette.mode === "dark"
        ? `rgba(${parseInt(colors.primary[400].slice(1, 3), 16)}, ${parseInt(colors.primary[400].slice(3, 5), 16)}, ${parseInt(colors.primary[400].slice(5, 7), 16)}, 0.8)`
        : "rgba(255, 255, 255, 0.95)",
    backdropFilter: "blur(16px)",
    border: `1px solid ${
      theme.palette.mode === "dark"
        ? "rgba(255, 255, 255, 0.15)"
        : "rgba(0, 0, 0, 0.1)"
    }`,
    boxShadow:
      theme.palette.mode === "dark"
        ? "0 8px 32px 0 rgba(0, 0, 0, 0.4)"
        : "0 8px 32px 0 rgba(0, 0, 0, 0.15)",
    borderRadius: "12px",
    mt: 1,
    "& .MuiAutocomplete-listbox": {
      padding: "8px",
      "& .MuiAutocomplete-option": {
        padding: "12px 20px",
        margin: "4px 8px",
        borderRadius: "8px",
        transition: "all 0.2s ease-in-out",
        color: colors.gray[100],
        fontWeight: 600,
        fontSize: "14px",
        "&[aria-selected='true']": {
          backgroundColor:
            theme.palette.mode === "dark"
              ? "rgba(255, 255, 255, 0.15)"
              : "rgba(0, 0, 0, 0.08)",
          color: colors.blueAccent[500],
        },
        "&[aria-selected='true'].Mui-focused": {
          backgroundColor:
            theme.palette.mode === "dark"
              ? "rgba(255, 255, 255, 0.2)"
              : "rgba(0, 0, 0, 0.12)",
        },
        "&:hover, &.Mui-focused": {
          backgroundColor:
            theme.palette.mode === "dark"
              ? "rgba(255, 255, 255, 0.1)"
              : "rgba(0, 0, 0, 0.04)",
        },
        "&:active": { transform: "scale(0.95)" },
      },
    },
    "& .MuiMenuItem-root": {
      padding: "12px 20px",
      margin: "4px 8px",
      borderRadius: "8px",
      transition: "all 0.2s ease-in-out",
      color: colors.gray[100],
      fontWeight: 600,
      fontSize: "14px",
      "&:hover": {
        backgroundColor:
          theme.palette.mode === "dark"
            ? "rgba(255, 255, 255, 0.1)"
            : "rgba(0, 0, 0, 0.04)",
      },
      "&:active": { transform: "scale(0.95)" },
      "&.Mui-selected": {
        backgroundColor:
          theme.palette.mode === "dark"
            ? "rgba(255, 255, 255, 0.15)"
            : "rgba(0, 0, 0, 0.08)",
        color: colors.blueAccent[500],
      },
      "&.Mui-selected:hover": {
        backgroundColor:
          theme.palette.mode === "dark"
            ? "rgba(255, 255, 255, 0.2)"
            : "rgba(0, 0, 0, 0.12)",
      },
    },
  };

  // Custom Step Icon Component
  const CustomStepIcon = (props) => {
    const { active, completed, className, icon } = props;

    return (
      <Box
        className={className}
        sx={{
          zIndex: 1,
          width: 32,
          height: 32,
          display: "flex",
          borderRadius: "50%",
          justifyContent: "center",
          alignItems: "center",
          fontWeight: "bold",
          fontSize: "14px",
          backgroundColor: completed
            ? colors.greenAccent[500]
            : active
              ? colors.blueAccent[600]
              : theme.palette.mode === "dark"
                ? "rgba(255,255,255,0.08)"
                : "rgba(0,0,0,0.06)",
          color: completed || active ? "#ffffff" : colors.gray[300],
          boxShadow: active
            ? `0 0 0 4px ${
                theme.palette.mode === "dark"
                  ? "rgba(99, 102, 241, 0.25)"
                  : "rgba(79, 70, 229, 0.2)"
              }`
            : "none",
          transition: "all 0.3s ease",
        }}
      >
        {completed ? (
          <CheckOutlinedIcon sx={{ fontSize: "18px", strokeWidth: 2 }} />
        ) : (
          icon
        )}
      </Box>
    );
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={(event, reason) => {
          if (reason === "backdropClick" || reason === "escapeKeyDown") return;
          if (phase !== "loading") handleCloseDialog();
        }}
        maxWidth="md"
        fullWidth
        TransitionComponent={Transition}
        keepMounted
        PaperProps={{
          sx: {
            backgroundColor:
              theme.palette.mode === "dark" ? colors.primary[400] : "#ffffff",
            backgroundImage: "none",
            borderRadius: "12px",
            boxShadow: "0 12px 40px rgba(0,0,0,0.3)",
            border: `1px solid ${
              theme.palette.mode === "dark"
                ? "rgba(255,255,255,0.05)"
                : "rgba(0,0,0,0.05)"
            }`,
          },
        }}
      >
        <DialogTitle
          textAlign="center"
          sx={{ fontSize: "1.25rem", fontWeight: "700", pt: 3, pb: 2 }}
        >
          {editMode ? "Edit Sales Record" : "Add New Sales Record"}
        </DialogTitle>

        <Divider
          sx={{
            borderColor:
              theme.palette.mode === "dark"
                ? "rgba(255,255,255,0.1)"
                : "rgba(0,0,0,0.12)",
          }}
        />

        <DialogContent sx={{ p: { xs: 2, sm: 3 } }}>
          <Stepper
            activeStep={activeStep}
            alternativeLabel
            connector={
              <StepConnector
                sx={{
                  "& .MuiStepConnector-line": {
                    borderColor:
                      theme.palette.mode === "dark"
                        ? "rgba(255,255,255,0.1)"
                        : "rgba(0,0,0,0.1)",
                    borderTopWidth: 3,
                    borderRadius: 2,
                    transition: "border-color 0.3s ease",
                  },
                  "&.Mui-active .MuiStepConnector-line": {
                    borderColor: colors.blueAccent[500],
                  },
                  "&.Mui-completed .MuiStepConnector-line": {
                    borderColor: colors.greenAccent[500],
                  },
                }}
              />
            }
            sx={{
              mb: 4,
              "& .MuiStepLabel-label": {
                fontWeight: 600,
                color: colors.gray[400],
                mt: "10px !important",
                transition: "color 0.3s ease",
                "&.Mui-active": { color: colors.blueAccent[500] },
                "&.Mui-completed": { color: colors.greenAccent[500] },
              },
            }}
          >
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel StepIconComponent={CustomStepIcon}>
                  {label}
                </StepLabel>
              </Step>
            ))}
          </Stepper>

          <Formik
            innerRef={formikRef}
            initialValues={initialValues}
            enableReinitialize
            validationSchema={validationSchema}
            onSubmit={handleSubmit}
          >
            {({
              values,
              errors,
              touched,
              handleBlur,
              handleChange,
              setFieldValue,
              setFieldTouched,
              handleSubmit,
              isSubmitting,
            }) => {
              return (
                <form
                  onSubmit={handleSubmit}
                  onKeyDown={(e) => {
                    if (
                      e.key === "Enter" &&
                      e.target.tagName !== "TEXTAREA" &&
                      activeStep < steps.length - 1
                    ) {
                      e.preventDefault();
                    }
                  }}
                >
                  {/* Step 1 - Customer Info */}
                  {activeStep === 0 && (
                    <Box
                      display="grid"
                      gap="16px"
                      gridTemplateColumns="repeat(4, 1fr)"
                      sx={{ animation: "fadeIn 0.3s ease-out" }}
                    >
                      <Box
                        sx={{
                          gridColumn: { xs: "span 4", sm: "span 2" },
                          display: "flex",
                          gap: 1,
                          alignItems: "center",
                          minWidth: 0,
                        }}
                      >
                        <Autocomplete
                          freeSolo
                          options={uniqueCustomers}
                          slotProps={{ paper: { sx: dropdownPaperStyle } }}
                          getOptionLabel={(option) => {
                            if (typeof option === "string") return option;
                            if (option.inputValue) return option.inputValue;
                            return option?.fullName ?? "";
                          }}
                          filterOptions={(options, params) => {
                            const filtered = filter(options, params);
                            const { inputValue } = params;
                            const isExisting = options.some((opt) => {
                              const name =
                                typeof opt === "string"
                                  ? opt
                                  : (opt?.fullName ?? "");
                              return (
                                name.toLowerCase() === inputValue.toLowerCase()
                              );
                            });
                            if (inputValue !== "" && !isExisting) {
                              filtered.push({
                                inputValue,
                                fullName: `Add "${inputValue}"`,
                                __isAddOption: true,
                              });
                            }
                            return filtered;
                          }}
                          isOptionEqualToValue={(option, value) => {
                            if (!option || !value) return false;
                            if (option.id != null && value.id != null)
                              return option.id === value.id;
                            return (
                              (option.fullName ?? "").toString() ===
                              (value.fullName ?? "").toString()
                            );
                          }}
                          value={selectedCustomerObj}
                          open={customerAutocompleteOpen}
                          onOpen={() => setCustomerAutocompleteOpen(true)}
                          onClose={() => setCustomerAutocompleteOpen(false)}
                          onInputChange={(event, inputValue, reason) => {
                            if (reason === "input") {
                              setSelectedCustomerObj(null);
                              setSelectedCustomerId(null);
                              setFieldValue("customerName", inputValue || "");
                            }
                          }}
                          onChange={(event, newValue) => {
                            if (!newValue) {
                              setSelectedCustomerObj(null);
                              setFieldValue("customerName", "");
                              setFieldValue("customerEmail", "");
                              setSelectedCustomerId(null);
                              return;
                            }

                            if (
                              typeof newValue === "object" &&
                              newValue.__isAddOption
                            ) {
                              setCustomerAutocompleteOpen(false);
                              const input = document.querySelector(
                                'input[name="customerName"]',
                              );
                              if (input) input.blur();

                              const typed =
                                newValue.inputValue ?? newValue.fullName ?? "";
                              setCustomerDialogPrefill(typed);
                              setTimeout(() => {
                                setOpenCustomerModal(true);
                              }, 0);
                              return;
                            }

                            if (typeof newValue === "string") {
                              const name = newValue.trim();
                              setSelectedCustomerObj({
                                id: null,
                                fullName: name,
                                email: "",
                              });
                              setFieldValue("customerName", name);
                              setSelectedCustomerId(null);
                              return;
                            }

                            setSelectedCustomerObj(newValue);
                            setSelectedCustomerId(newValue.id ?? null);
                            setFieldValue(
                              "customerName",
                              newValue.fullName ?? "",
                            );

                            if (
                              newValue.email != null &&
                              newValue.email !== ""
                            ) {
                              setFieldValue(
                                "customerEmail",
                                String(newValue.email),
                              );
                            }
                          }}
                          renderOption={(props, option) => {
                            if (option && option.__isAddOption) {
                              return (
                                <li {...props} key={"add-" + option.inputValue}>
                                  <div style={{ fontWeight: 600 }}>
                                    {option.fullName}
                                  </div>
                                  <small
                                    style={{ color: colors.blueAccent[400] }}
                                  >
                                    + Click to add new customer
                                  </small>
                                </li>
                              );
                            }
                            return (
                              <li {...props} key={option.id ?? option.fullName}>
                                <div>
                                  <div>{option.fullName}</div>
                                  <small style={{ color: colors.gray[400] }}>
                                    {option.email || "No contact"}
                                  </small>
                                </div>
                              </li>
                            );
                          }}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              name="customerName"
                              label="Customer Name"
                              variant="filled"
                              fullWidth
                              sx={{ ...inputStyleProps }}
                              InputProps={{
                                ...params.InputProps,
                                disableUnderline: true,
                                style: {
                                  borderRadius: "8px",
                                  fontSize: "0.95rem",
                                },
                              }}
                              InputLabelProps={{
                                style: { fontSize: "0.95rem" },
                              }}
                            />
                          )}
                          sx={{
                            flex: 1,
                            minWidth: 0,
                            width: "100%",
                          }}
                          selectOnFocus
                          clearOnBlur
                          handleHomeEndKeys
                          openOnFocus
                          autoHighlight
                        />
                      </Box>

                      <TextField
                        fullWidth
                        variant="filled"
                        label="Customer Email (optional)"
                        name="customerEmail"
                        type="text"
                        onBlur={handleBlur}
                        onChange={(e) => {
                          handleChange(e);
                          if (selectedCustomerObj) {
                            setSelectedCustomerObj(null);
                            setSelectedCustomerId(null);
                          }
                        }}
                        value={
                          selectedCustomerObj?.email ||
                          values.customerEmail ||
                          ""
                        }
                        error={
                          !!touched.customerEmail && !!errors.customerEmail
                        }
                        helperText={
                          touched.customerEmail && errors.customerEmail
                        }
                        sx={{
                          ...inputStyleProps,
                          gridColumn: { xs: "span 4", sm: "span 2" },
                        }}
                        InputProps={{
                          disableUnderline: true,
                          style: { borderRadius: "8px", fontSize: "0.95rem" },
                        }}
                        InputLabelProps={{ style: { fontSize: "0.95rem" } }}
                      />
                    </Box>
                  )}

                  {/* Step 2 - Product Info */}
                  {activeStep === 1 && (
                    <Box
                      display="grid"
                      gap="16px"
                      gridTemplateColumns="repeat(4, 1fr)"
                      sx={{ animation: "fadeIn 0.3s ease-out" }}
                    >
                      <Autocomplete
                        freeSolo
                        options={uniqueProducts}
                        slotProps={{ paper: { sx: dropdownPaperStyle } }}
                        getOptionLabel={(option) => {
                          if (typeof option === "string") return option;
                          return option?.productName ?? "";
                        }}
                        isOptionEqualToValue={(option, value) => {
                          if (!option || !value) return false;
                          if (option.id != null && value.id != null)
                            return option.id === value.id;
                          return (
                            (option.productName ?? "") ===
                            (value.productName ?? "")
                          );
                        }}
                        value={selectedProduct}
                        onInputChange={(event, newInputValue, reason) => {
                          if (reason === "input") {
                            setSelectedProduct(null);
                            setFieldValue("title", newInputValue || "");
                            setFieldValue("productId", null);
                          }
                        }}
                        onChange={(event, newValue) => {
                          if (!newValue) {
                            setSelectedProduct(null);
                            setFieldValue("title", "");
                            setFieldValue("productId", null);
                            return;
                          }

                          if (typeof newValue === "string") {
                            setSelectedProduct(null);
                            setFieldValue("title", newValue);
                            setFieldValue("productId", null);
                            return;
                          }

                          setSelectedProduct(newValue);
                          setFieldValue("title", newValue.productName ?? "");
                          setFieldValue("productId", newValue.id ?? null);
                          setFieldValue(
                            "type",
                            newValue.category || values.type || "",
                          );
                          setFieldValue(
                            "rate",
                            newValue.cost != null
                              ? newValue.cost
                              : (values.rate ?? ""),
                          );
                        }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Product Title"
                            variant="filled"
                            name="title"
                            onBlur={handleBlur}
                            error={!!touched.title && !!errors.title}
                            helperText={touched.title && errors.title}
                            sx={{ ...inputStyleProps }}
                            InputProps={{
                              ...params.InputProps,
                              disableUnderline: true,
                              style: {
                                borderRadius: "8px",
                                fontSize: "0.95rem",
                              },
                            }}
                            InputLabelProps={{ style: { fontSize: "0.95rem" } }}
                          />
                        )}
                        renderOption={(props, option) => (
                          <li {...props} key={option.id ?? option.productName}>
                            <div>
                              <div>{option.productName}</div>
                              <small style={{ color: colors.gray[400] }}>
                                {option.category ? `${option.category} • ` : ""}
                                {option.cost != null
                                  ? `Rate: ₦${Number(option.cost).toLocaleString()} • `
                                  : "No rate • "}
                                {option.quantity !== 0
                                  ? `Stock: ${option.quantity} `
                                  : "Out of stock"}
                              </small>
                            </div>
                          </li>
                        )}
                        sx={{ gridColumn: { xs: "span 4", sm: "span 2" } }}
                      />

                      <Autocomplete
                        freeSolo
                        options={["Cement", "Building Blocks"]}
                        value={values.type}
                        slotProps={{ paper: { sx: dropdownPaperStyle } }}
                        onChange={(event, newValue) =>
                          setFieldValue("type", newValue)
                        }
                        onInputChange={(event, newInputValue) =>
                          setFieldValue("type", newInputValue)
                        }
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            variant="filled"
                            label="Type"
                            name="type"
                            onBlur={handleBlur}
                            error={!!touched.type && !!errors.type}
                            helperText={touched.type && errors.type}
                            sx={{ ...inputStyleProps }}
                            InputProps={{
                              ...params.InputProps,
                              disableUnderline: true,
                              style: {
                                borderRadius: "8px",
                                fontSize: "0.95rem",
                              },
                            }}
                            InputLabelProps={{ style: { fontSize: "0.95rem" } }}
                          />
                        )}
                        sx={{ gridColumn: { xs: "span 4", sm: "span 2" } }}
                      />

                      <TextField
                        fullWidth
                        variant="filled"
                        type="number"
                        label="Quantity"
                        name="quantity"
                        onBlur={handleBlur}
                        onChange={(event) => {
                          const quantity = parseFloat(event.target.value || 0);
                          setFieldValue("quantity", quantity);

                          const rate = Number(values.rate || 0);
                          const totalBalance = rate * quantity;
                          const amountPaid = Number(values.amountPaid || 0);

                          if (values.paymentStatus === "PENDING_PAYMENT") {
                            setFieldValue("outstandingBalance", totalBalance);
                            setFieldValue("amountPaid", 0);
                          } else if (
                            values.paymentStatus === "PARTIAL_PAYMENT"
                          ) {
                            setFieldValue(
                              "outstandingBalance",
                              totalBalance - amountPaid,
                            );
                          } else if (values.paymentStatus === "FULLY_PAID") {
                            setFieldValue("outstandingBalance", 0);
                            setFieldValue("amountPaid", totalBalance);
                          }
                        }}
                        value={values.quantity}
                        error={!!touched.quantity && !!errors.quantity}
                        helperText={touched.quantity && errors.quantity}
                        sx={{
                          gridColumn: { xs: "span 4", sm: "span 2" },
                          ...inputStyleProps,
                        }}
                        inputProps={{ min: 0 }}
                        InputProps={{
                          disableUnderline: true,
                          style: { borderRadius: "8px", fontSize: "0.95rem" },
                        }}
                        InputLabelProps={{ style: { fontSize: "0.95rem" } }}
                      />

                      <TextField
                        fullWidth
                        variant="filled"
                        type="number"
                        label="Rate"
                        name="rate"
                        onBlur={handleBlur}
                        onChange={(event) => {
                          const newRate = parseFloat(event.target.value || 0);
                          setFieldValue("rate", newRate);

                          const newTotalBalance =
                            newRate * (values.quantity || 0);
                          const amountPaid = Number(values.amountPaid || 0);

                          if (values.paymentStatus === "PENDING_PAYMENT") {
                            setFieldValue(
                              "outstandingBalance",
                              newTotalBalance,
                            );
                          } else if (
                            values.paymentStatus === "PARTIAL_PAYMENT"
                          ) {
                            setFieldValue(
                              "outstandingBalance",
                              newTotalBalance - amountPaid,
                            );
                          } else if (values.paymentStatus === "FULLY_PAID") {
                            setFieldValue("outstandingBalance", 0);
                            setFieldValue("amountPaid", newTotalBalance);
                          }
                        }}
                        value={values.rate}
                        sx={{
                          gridColumn: { xs: "span 4", sm: "span 2" },
                          ...inputStyleProps,
                        }}
                        inputProps={{ min: 0 }}
                        InputProps={{
                          disableUnderline: true,
                          style: { borderRadius: "8px", fontSize: "0.95rem" },
                        }}
                        InputLabelProps={{ style: { fontSize: "0.95rem" } }}
                      />

                      <CustomDatePicker
                        label="Date"
                        name="date"
                        value={values.date}
                        onChange={(val) => setFieldValue("date", val)}
                        onBlur={handleBlur}
                        error={!!touched.date && !!errors.date}
                        helperText={touched.date && errors.date}
                        sx={{
                          gridColumn: { xs: "span 4", sm: "span 2" },
                        }}
                      />

                      <TextField
                        fullWidth
                        variant="filled"
                        type="text"
                        label="Description"
                        name="salesDesc"
                        onBlur={handleBlur}
                        onChange={handleChange}
                        value={values.salesDesc}
                        sx={{
                          gridColumn: { xs: "span 4", sm: "span 2" },
                          ...inputStyleProps,
                        }}
                        InputProps={{
                          disableUnderline: true,
                          style: { borderRadius: "8px", fontSize: "0.95rem" },
                        }}
                        InputLabelProps={{ style: { fontSize: "0.95rem" } }}
                      />

                      <TextField
                        fullWidth
                        variant="filled"
                        type="text" // Using text to format nicely, even though logic is numeric
                        label="Total Balance (Auto)"
                        name="totalBalance"
                        value={new Intl.NumberFormat("en-NG", {
                          style: "currency",
                          currency: "NGN",
                        }).format(
                          Number(values.quantity || 0) *
                            Number(values.rate || 0),
                        )}
                        InputProps={{
                          readOnly: true,
                          disableUnderline: true,
                          style: {
                            borderRadius: "8px",
                            fontSize: "0.95rem",
                            fontWeight: "bold",
                            color: colors.greenAccent[500],
                          },
                        }}
                        sx={{
                          gridColumn: "span 4",
                          ...inputStyleProps,
                          opacity: 0.8,
                        }}
                        InputLabelProps={{ style: { fontSize: "0.95rem" } }}
                      />
                    </Box>
                  )}

                  {/* Step 3 - Payment Details */}
                  {activeStep === 2 && (
                    <Box
                      display="grid"
                      gap="16px"
                      gridTemplateColumns="repeat(4, 1fr)"
                      sx={{ animation: "fadeIn 0.3s ease-out" }}
                    >
                      <FormControl
                        fullWidth
                        variant="filled"
                        sx={{
                          ...inputStyleProps,
                          gridColumn: { xs: "span 4", sm: "span 2" },
                        }}
                      >
                        <InputLabel
                          id="payment-status-label"
                          style={{ fontSize: "0.95rem" }}
                        >
                          Payment Status
                        </InputLabel>
                        <Select
                          labelId="payment-status-label"
                          name="paymentStatus"
                          value={values.paymentStatus}
                          onChange={(event) => {
                            const paymentStatus = event.target.value;
                            setFieldValue("paymentStatus", paymentStatus);

                            const totalBalance =
                              Number(values.rate || 0) *
                              Number(values.quantity || 0);
                            if (paymentStatus === "PENDING_PAYMENT") {
                              setFieldValue("outstandingBalance", totalBalance);
                              setFieldValue("amountPaid", 0);
                              setFieldValue("installmentPlan", "INACTIVE");
                            } else if (paymentStatus === "PARTIAL_PAYMENT") {
                              setFieldValue("amountPaid", 0);
                              setFieldValue("outstandingBalance", totalBalance);
                              setFieldValue("installmentPlan", "ACTIVE");
                            } else if (paymentStatus === "FULLY_PAID") {
                              setFieldValue("amountPaid", totalBalance);
                              setFieldValue("outstandingBalance", 0);
                              setFieldValue("installmentPlan", "COMPLETED");
                            }
                          }}
                          onBlur={handleBlur}
                          disableUnderline
                          sx={{ borderRadius: "8px", fontSize: "0.95rem" }}
                          MenuProps={{
                            TransitionComponent: Fade,
                            transitionDuration: 300,
                            PaperProps: { sx: dropdownPaperStyle },
                          }}
                        >
                          <MenuItem value="PENDING_PAYMENT">
                            Pending Payment
                          </MenuItem>
                          <MenuItem value="PARTIAL_PAYMENT">
                            Partial Payment (Installment)
                          </MenuItem>
                          <MenuItem value="FULLY_PAID">Fully Paid</MenuItem>
                        </Select>
                      </FormControl>

                      <TextField
                        fullWidth
                        variant="filled"
                        type="number"
                        label="Amount Paid"
                        name="amountPaid"
                        onBlur={handleBlur}
                        onChange={(event) => {
                          const amountPaid = parseFloat(
                            event.target.value || 0,
                          );
                          setFieldValue("amountPaid", amountPaid);
                          if (values.paymentStatus === "PARTIAL_PAYMENT") {
                            const totalBalance =
                              Number(values.rate || 0) *
                              Number(values.quantity || 0);
                            setFieldValue(
                              "outstandingBalance",
                              totalBalance - amountPaid,
                            );
                          }
                        }}
                        value={values.amountPaid}
                        disabled={values.paymentStatus === "FULLY_PAID"}
                        sx={{
                          ...inputStyleProps,
                          gridColumn: { xs: "span 4", sm: "span 2" },
                        }}
                        inputProps={{ min: 0 }}
                        InputProps={{
                          disableUnderline: true,
                          style: { borderRadius: "8px", fontSize: "0.95rem" },
                        }}
                        InputLabelProps={{ style: { fontSize: "0.95rem" } }}
                      />

                      <TextField
                        fullWidth
                        variant="filled"
                        type="number"
                        label="Outstanding Balance"
                        name="outstandingBalance"
                        value={values.outstandingBalance}
                        onBlur={handleBlur}
                        onChange={(event) =>
                          setFieldValue(
                            "outstandingBalance",
                            parseFloat(event.target.value || 0),
                          )
                        }
                        disabled={values.paymentStatus === "FULLY_PAID"}
                        sx={{
                          ...inputStyleProps,
                          gridColumn: { xs: "span 4", sm: "span 2" },
                        }}
                        inputProps={{ min: 0 }}
                        InputProps={{
                          disableUnderline: true,
                          style: { borderRadius: "8px", fontSize: "0.95rem" },
                        }}
                        InputLabelProps={{ style: { fontSize: "0.95rem" } }}
                      />

                      <FormControl
                        fullWidth
                        variant="filled"
                        disabled={values.paymentStatus === "FULLY_PAID"}
                        sx={{
                          ...inputStyleProps,
                          gridColumn: { xs: "span 4", sm: "span 2" },
                        }}
                      >
                        <InputLabel
                          id="payment-frequency-label"
                          style={{ fontSize: "0.95rem" }}
                        >
                          Payment Frequency
                        </InputLabel>
                        <Select
                          labelId="payment-frequency-label"
                          name="paymentFrequency"
                          value={values.paymentFrequency}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          disableUnderline
                          sx={{ borderRadius: "8px", fontSize: "0.95rem" }}
                          MenuProps={{
                            TransitionComponent: Fade,
                            transitionDuration: 300,
                            PaperProps: { sx: dropdownPaperStyle },
                          }}
                        >
                          <MenuItem value="WEEKLY">Weekly</MenuItem>
                          <MenuItem value="BIWEEKLY">Twice a Month</MenuItem>
                          <MenuItem value="MONTHLY">Monthly</MenuItem>
                          <MenuItem value="QUARTERLY">Every 3 Months</MenuItem>
                        </Select>
                      </FormControl>

                      <FormControl
                        fullWidth
                        variant="filled"
                        disabled={values.paymentStatus === "FULLY_PAID"}
                        sx={{
                          ...inputStyleProps,
                          gridColumn: { xs: "span 4", sm: "span 2" },
                        }}
                      >
                        <InputLabel
                          id="installment-duration-label"
                          style={{ fontSize: "0.95rem" }}
                        >
                          Installment Plan Duration
                        </InputLabel>
                        <Select
                          labelId="installment-duration-label"
                          name="installmentDuration"
                          value={values.installmentDuration}
                          onChange={(e) => {
                            handleChange(e);
                            if (e.target.value !== "Custom") {
                              setFieldValue("customInstallmentDuration", "");
                            }
                          }}
                          onBlur={handleBlur}
                          disableUnderline
                          sx={{ borderRadius: "8px", fontSize: "0.95rem" }}
                          MenuProps={{
                            TransitionComponent: Fade,
                            transitionDuration: 300,
                            PaperProps: { sx: dropdownPaperStyle },
                          }}
                        >
                          <MenuItem value="1 Month">1 Month</MenuItem>
                          <MenuItem value="3 Months">3 Months</MenuItem>
                          <MenuItem value="6 Months">6 Months</MenuItem>
                          <MenuItem value="1 Year">1 Year</MenuItem>
                          <MenuItem value="Custom">Other (Custom)</MenuItem>
                        </Select>
                      </FormControl>

                      {values.installmentDuration === "Custom" && (
                        <TextField
                          fullWidth
                          variant="filled"
                          type="number"
                          label="Custom Duration (days)"
                          name="customInstallmentDuration"
                          value={values.customInstallmentDuration}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          sx={{
                            ...inputStyleProps,
                            gridColumn: { xs: "span 4", sm: "span 2" },
                          }}
                          InputProps={{
                            disableUnderline: true,
                            style: { borderRadius: "8px", fontSize: "0.95rem" },
                          }}
                          InputLabelProps={{ style: { fontSize: "0.95rem" } }}
                        />
                      )}
                    </Box>
                  )}

                  <Divider
                    sx={{
                      mt: 4,
                      mb: 0,
                      mx: { xs: -2, sm: -3 },
                      borderColor:
                        theme.palette.mode === "dark"
                          ? "rgba(255,255,255,0.1)"
                          : "rgba(0,0,0,0.12)",
                    }}
                  />

                  {/* Actions */}
                  <DialogActions
                    sx={{
                      pt: 2,
                      pb: 0,
                      px: 0,
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <Button
                      type="button"
                      onClick={handleCloseDialog}
                      disabled={phase === "loading"}
                      sx={{
                        color: colors.gray[100],
                        textTransform: "none",
                        fontWeight: "600",
                        px: 2,
                        borderRadius: "8px",
                        transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                        "&:hover": {
                          backgroundColor:
                            theme.palette.mode === "dark"
                              ? "rgba(255,255,255,0.05)"
                              : "rgba(0,0,0,0.04)",
                        },
                      }}
                    >
                      Cancel
                    </Button>

                    <Box display="flex" gap={1}>
                      {activeStep > 0 && (
                        <Button
                          type="button"
                          onClick={prevStep}
                          disabled={phase === "loading"}
                          variant="outlined"
                          sx={{
                            color: colors.gray[100],
                            borderColor: colors.gray[500],
                            textTransform: "none",
                            fontWeight: "600",
                            px: { xs: 2, sm: 3 },
                            py: { xs: 1, sm: 1.2 },
                            borderRadius: "8px",
                            transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                            "&:hover": {
                              borderColor: colors.gray[300],
                              backgroundColor:
                                theme.palette.mode === "dark"
                                  ? "rgba(255,255,255,0.05)"
                                  : "rgba(0,0,0,0.04)",
                            },
                          }}
                        >
                          Back
                        </Button>
                      )}

                      {activeStep < steps.length - 1 ? (
                        <Button
                          type="button"
                          variant="contained"
                          onClick={(e) => {
                            e.preventDefault();
                            nextStep(values, errors, setFieldTouched);
                          }}
                          sx={{
                            backgroundColor: colors.blueAccent[600],
                            color: "#ffffff",
                            textTransform: "none",
                            fontWeight: "600",
                            px: { xs: 3, sm: 4 },
                            py: { xs: 1, sm: 1.2 },
                            borderRadius: "8px",
                            boxShadow:
                              theme.palette.mode === "dark"
                                ? "0 4px 10px rgba(0,0,0,0.3)"
                                : "0 4px 12px rgba(79, 70, 229, 0.2)",
                            transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                            "&:hover": {
                              backgroundColor: colors.blueAccent[500],
                              boxShadow:
                                theme.palette.mode === "dark"
                                  ? "0 6px 14px rgba(0,0,0,0.4)"
                                  : "0 6px 16px rgba(79, 70, 229, 0.3)",
                              transform: "translateY(-1px)",
                            },
                            "&:active": {
                              transform: "scale(0.95)",
                              boxShadow: "none",
                            },
                          }}
                        >
                          Next
                        </Button>
                      ) : (
                        <Button
                          variant="contained"
                          type="submit"
                          disabled={phase === "loading"}
                          sx={{
                            backgroundColor: colors.blueAccent[600],
                            color: "#ffffff",
                            textTransform: "none",
                            fontWeight: "600",
                            px: { xs: 3, sm: 4 },
                            py: { xs: 1, sm: 1.2 },
                            borderRadius: "8px",
                            boxShadow:
                              theme.palette.mode === "dark"
                                ? "0 4px 10px rgba(0,0,0,0.3)"
                                : "0 4px 12px rgba(79, 70, 229, 0.2)",
                            transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                            "&:hover": {
                              backgroundColor: colors.blueAccent[500],
                              boxShadow:
                                theme.palette.mode === "dark"
                                  ? "0 6px 14px rgba(0,0,0,0.4)"
                                  : "0 6px 16px rgba(79, 70, 229, 0.3)",
                              transform: "translateY(-1px)",
                            },
                            "&:active": {
                              transform: "scale(0.95)",
                              boxShadow: "none",
                            },
                            "&.Mui-disabled": {
                              backgroundColor:
                                theme.palette.mode === "dark"
                                  ? "rgba(255,255,255,0.12)"
                                  : "rgba(0,0,0,0.12)",
                              color:
                                theme.palette.mode === "dark"
                                  ? "rgba(255,255,255,0.3)"
                                  : "rgba(0,0,0,0.3)",
                              boxShadow: "none",
                            },
                          }}
                        >
                          {phase === "loading" ? (
                            <>
                              {editMode ? "Updating" : "Creating"}&nbsp;
                              <CircularProgress
                                size={18}
                                sx={{ color: "inherit", ml: "8px" }}
                              />
                            </>
                          ) : editMode ? (
                            "Update"
                          ) : (
                            "Submit"
                          )}
                        </Button>
                      )}
                    </Box>
                  </DialogActions>
                </form>
              );
            }}
          </Formik>
        </DialogContent>
      </Dialog>

      <CustomerForm
        open={openCustomerModal}
        handleClose={handleCloseCustomerModal}
        onCustomerAdded={handleCustomerAdded}
        prefillName={customerDialogPrefill}
      />

      <Portal>
        <Snackbar
          open={snackbarOpen}
          autoHideDuration={3000}
          onClose={handleSnackbarClose}
          anchorOrigin={{ vertical: "top", horizontal: "right" }}
          TransitionComponent={SlideTransition}
          sx={{ mt: 8, zIndex: 9999 }}
        >
          <Alert
            onClose={handleSnackbarClose}
            severity={snackbarSeverity}
            sx={{
              width: "100%",
              backgroundColor:
                snackbarSeverity === "success"
                  ? colors.greenAccent[700]
                  : colors.redAccent[700],
              color: "#ffffff",
              borderRadius: "8px",
              fontWeight: 600,
              boxShadow:
                theme.palette.mode === "dark"
                  ? "0px 4px 12px rgba(0, 0, 0, 0.4)"
                  : "0px 4px 12px rgba(0, 0, 0, 0.1)",
            }}
          >
            {snackbarMessage}
          </Alert>
        </Snackbar>
      </Portal>

      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </>
  );
};

export default SalesRecordForm;
